import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select,
  Stack, Tab, Tabs, TextField, Typography, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import type { ClusterSummary } from '@residenone/shared';
import { useLocation } from 'react-router-dom';
import { getMyClusters } from '../../lib/clusters-api';
import { isLegacyCompany } from '../../lib/platform-api';
import {
  createTeamInvitation, listInvitationUnits, listTeamMembers, removeTeamMember,
  STAFF_PERMISSIONS, type StaffPermission, type TeamMember, type TeamRole,
} from '../../lib/team-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';
import { labelRepeatsAddress } from '../../lib/unit-address';
import { ResidentImportPanel } from './ResidentImport';

// The cluster team (admins, staff, security) and the invite form. Both live on
// the Warga page: inviting anyone, resident or team, starts from there.

export interface TeamIdentity { id?: string; email?: string; admin_scope?: string; app_role?: string; permissions?: string[] }

interface DeveloperOption { id: string; name: string }

const managesMany = (identity?: TeamIdentity) => identity?.admin_scope === 'PLATFORM' || identity?.admin_scope === 'DEVELOPER';

/**
 * Which developer and cluster the team list shows, and which clusters there
 * are to invite into. Platform and company admins can switch between them;
 * a cluster admin just has their own.
 */
export function useTeamScope(identity: TeamIdentity | undefined, enabled: boolean) {
  const location = useLocation();
  const [clusters, setClusters] = useState<ClusterSummary[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [developerId, setDeveloperId] = useState('');
  const [clusterId, setClusterId] = useState('');

  useEffect(() => {
    if (!enabled) return;
    getMyClusters().then((result) => {
      setClusters([...new Map(result.clusters
        .filter((cluster) => !isLegacyCompany(cluster.developer_id ?? ''))
        .map((cluster) => [cluster.id, cluster])).values()]);
    }).catch((err: unknown) => setError(extractApiError(err).message)).finally(() => setLoading(false));
  }, [enabled]);

  const developers = useMemo<DeveloperOption[]>(() => {
    const byId = new Map<string, DeveloperOption>();
    for (const cluster of clusters) {
      if (cluster.developer_id && !byId.has(cluster.developer_id)) {
        byId.set(cluster.developer_id, { id: cluster.developer_id, name: cluster.developer_name ?? cluster.developer_id });
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [clusters]);

  // Start from the developer/cluster in the address (search results link here), else the first.
  useEffect(() => {
    if (developers.length === 0) return;
    const params = new URLSearchParams(location.search);
    const requestedDeveloper = params.get('developer');
    const requestedCluster = params.get('cluster');
    const developer = developers.find((item) => item.id === requestedDeveloper)
      ?? developers.find((item) => clusters.some((cluster) => cluster.developer_id === item.id && cluster.id === requestedCluster))
      ?? developers[0]!;
    const ownClusters = clusters.filter((cluster) => cluster.developer_id === developer.id);
    setDeveloperId(developer.id);
    setClusterId(ownClusters.find((cluster) => cluster.id === requestedCluster)?.id
      ?? (managesMany(identity) && ownClusters.length > 1 ? 'all' : ownClusters[0]?.id ?? ''));
  }, [clusters, developers, identity, location.search]);

  const developerClusters = useMemo(() => clusters.filter((cluster) => cluster.developer_id === developerId), [clusters, developerId]);

  function chooseDeveloper(id: string) {
    const ownClusters = clusters.filter((cluster) => cluster.developer_id === id);
    setDeveloperId(id);
    setClusterId(managesMany(identity) && ownClusters.length > 1 ? 'all' : ownClusters[0]?.id ?? '');
  }

  return { loading, error, clusters, developers, developerId, clusterId, developerClusters, chooseDeveloper, setClusterId };
}

export type TeamScope = ReturnType<typeof useTeamScope>;

function roleLabel(member: TeamMember, t: ReturnType<typeof useT>): string {
  if (member.admin_scope === 'PLATFORM') return t('team.platformAdmin');
  if (member.admin_scope === 'DEVELOPER') return t('team.companyAdmin');
  if (member.role === 'STAFF' && member.app_role === 'ADMIN') return t('team.clusterAdmin');
  if (member.role === 'STAFF') return t('team.staff');
  return t('team.security');
}

// The members endpoint returns everyone in the cluster; residents have their
// own tab, so this list keeps the people who run it.
const teamOnly = (lists: TeamMember[][]) => lists.flat().filter((member) => member.role !== 'RESIDENT');

/** The team members of the chosen cluster(s), with removal. `version` reloads it after an invite. */
export function TeamMembersPanel({ scope, identity, version }: { scope: TeamScope; identity?: TeamIdentity; version: number }) {
  const t = useT();
  const location = useLocation();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { developers, developerId, clusterId, developerClusters } = scope;

  const viewedIds = clusterId === 'all'
    ? developerClusters.map((cluster) => cluster.id)
    : developerClusters.filter((cluster) => cluster.id === clusterId).map((cluster) => cluster.id);
  const viewedKey = viewedIds.join(',');

  useEffect(() => {
    if (!viewedKey) { setMembers([]); return; }
    let cancelled = false;
    setLoading(true);
    Promise.all(viewedKey.split(',').map(listTeamMembers))
      .then((lists) => { if (!cancelled) setMembers(teamOnly(lists)); })
      .catch((err: unknown) => { if (!cancelled) setError(extractApiError(err).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [viewedKey, version]);

  const focusedMember = new URLSearchParams(location.search).get('member');
  useEffect(() => {
    if (focusedMember && members.some((member) => member.id === focusedMember)) {
      document.getElementById(`team-member-${focusedMember}`)?.scrollIntoView({ block: 'center' });
    }
  }, [members, focusedMember]);

  async function remove(member: TeamMember) {
    setBusy(true); setError(null); setNotice(null);
    try {
      await removeTeamMember(member.id);
      setMembers(teamOnly(await Promise.all(viewedIds.map(listTeamMembers))));
      setNotice(t(member.admin_scope === 'DEVELOPER' ? 'team.removedDeveloperNotice' : 'team.removedClusterNotice',
        { name: member.full_name }));
    } catch (err) { setError(extractApiError(err).message); }
    finally { setBusy(false); }
  }

  if (scope.loading) return <CircularProgress />;
  if (scope.clusters.length === 0) return <Alert severity="info">{scope.error ?? t('team.createClusterFirst')}</Alert>;

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
      {notice ? <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert> : null}
      {/* Only those who look after more than one developer or cluster need to choose. */}
      {developers.length > 1 || developerClusters.length > 1 ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          {developers.length > 1 ? (
            <TextField select size="small" label={t('team.developer')} value={developerId}
              onChange={(event) => scope.chooseDeveloper(event.target.value)} sx={{ minWidth: 220 }}>
              {developers.map((developer) => <MenuItem key={developer.id} value={developer.id}>{developer.name}</MenuItem>)}
            </TextField>
          ) : null}
          {developerClusters.length > 1 ? (
            <TextField select size="small" label={t('team.cluster')} value={clusterId}
              onChange={(event) => scope.setClusterId(event.target.value)} sx={{ minWidth: 220 }}>
              <MenuItem value="all">{t('team.allClusters')}</MenuItem>
              {developerClusters.map((cluster) => <MenuItem key={cluster.id} value={cluster.id}>{cluster.name}</MenuItem>)}
            </TextField>
          ) : null}
        </Stack>
      ) : null}
      <Card variant="outlined"><CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : <Table><TableHead><TableRow>
          <TableCell>{t('team.fullName')}</TableCell><TableCell>{t('team.email')}</TableCell>
          {developerClusters.length > 1 ? <TableCell>{t('team.cluster')}</TableCell> : null}
          <TableCell>{t('team.role')}</TableCell><TableCell>{t('team.status')}</TableCell><TableCell />
        </TableRow></TableHead><TableBody>
          {members.map((member) => <TableRow key={member.id} id={`team-member-${member.id}`}
            sx={member.id === focusedMember ? { bgcolor: 'action.selected' } : undefined}>
            <TableCell>{member.full_name}</TableCell><TableCell>{member.email}</TableCell>
            {developerClusters.length > 1
              ? <TableCell>{developerClusters.find((cluster) => cluster.id === member.cluster_id)?.name ?? '—'}</TableCell>
              : null}
            <TableCell>{roleLabel(member, t)}</TableCell>
            <TableCell>{member.status}</TableCell>
            <TableCell align="right"><Button size="small" color="error"
              disabled={busy || member.id === identity?.id || member.email === identity?.email || member.admin_scope === 'PLATFORM'
                || (member.admin_scope === 'DEVELOPER' && identity?.admin_scope !== 'PLATFORM')
                || (member.app_role === 'ADMIN' && identity?.app_role !== 'ADMIN')}
              onClick={() => void remove(member)}>{t('team.remove')}</Button></TableCell>
          </TableRow>)}
          {members.length === 0 ? <TableRow><TableCell colSpan={6}>{t('team.noMembers')}</TableCell></TableRow> : null}
        </TableBody></Table>}
      </CardContent></Card>
    </Stack>
  );
}

/**
 * Invite a resident (to a unit) or a team member (admin, staff, security), or
 * many residents at once from a spreadsheet. Residents first: they're who
 * gets invited most often.
 */
export function InviteDialog({
  open, scope, identity, existingEmails, onClose, onInvited, onImported,
}: {
  open: boolean;
  scope: TeamScope;
  identity?: TeamIdentity;
  /** Emails already in the cluster, so the import can skip them. */
  existingEmails: string[];
  onClose: () => void;
  /** Someone was invited or assigned; `role` says which list should reload. */
  onInvited: (message: string, role: TeamRole) => void;
  /** The import sent some invitations; the resident list should reload. */
  onImported: () => void;
}) {
  const t = useT();
  const { developers } = scope;
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [developerId, setDeveloperId] = useState('');
  const [assignmentIds, setAssignmentIds] = useState<string[]>([]);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>('resident');
  const [permissions, setPermissions] = useState<StaffPermission[]>([]);
  const [units, setUnits] = useState<{ id: string; label: string; block: string; number: string }[]>([]);
  const [block, setBlock] = useState('');
  const [unitId, setUnitId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const developerClusters = useMemo(
    () => scope.clusters.filter((cluster) => cluster.developer_id === developerId),
    [scope.clusters, developerId],
  );

  // Each time it opens: a blank form for the developer and cluster on screen.
  useEffect(() => {
    if (!open) return;
    const devId = scope.developerId || developers[0]?.id || '';
    const own = scope.clusters.filter((cluster) => cluster.developer_id === devId);
    const start = own.find((cluster) => cluster.id === scope.clusterId) ?? own[0];
    setDeveloperId(devId);
    setAssignmentIds(start ? [start.id] : []);
    setMode('single');
    setFullName(''); setEmail(''); setRole('resident'); setPermissions([]); setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const residentClusterId = assignmentIds.length === 1 ? assignmentIds[0] : null;
  useEffect(() => {
    setUnitId('');
    setBlock('');
    if (!open || role !== 'resident' || !residentClusterId) { setUnits([]); return; }
    let cancelled = false;
    listInvitationUnits(residentClusterId)
      .then((items) => { if (!cancelled) setUnits(items); })
      .catch((err: unknown) => { if (!cancelled) setError(extractApiError(err).message); });
    return () => { cancelled = true; };
  }, [open, residentClusterId, role]);

  const blocks = useMemo(
    () => [...new Set(units.map((u) => u.block))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [units],
  );
  const unitsInBlock = useMemo(
    () => units.filter((u) => u.block === block).sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true })),
    [units, block],
  );

  const targetIds = role === 'company_admin' ? developerClusters.map((cluster) => cluster.id)
    : role === 'admin' ? assignmentIds : assignmentIds.slice(0, 1);
  // Offer only what this person may hand out; the server refuses the rest.
  const canAssignAdmins = identity?.app_role === 'ADMIN';
  const grantable = STAFF_PERMISSIONS.filter((item) => (identity?.permissions ?? []).includes(item.id));

  const canSubmit = Boolean(developerId && targetIds.length && fullName.trim() && email.trim()
    && (role !== 'resident' || unitId) && (role !== 'internal' || permissions.length));

  async function submit() {
    if (!targetIds.length) return;
    setBusy(true); setError(null);
    try {
      const result = await createTeamInvitation({
        developer_id: developerId, cluster_id: targetIds[0]!,
        ...(role === 'admin' ? { cluster_ids: targetIds } : {}),
        full_name: fullName.trim(), email: email.trim(), role,
        ...(role === 'internal' ? { permissions } : {}),
        ...(role === 'resident' ? { unit_id: unitId } : {}),
      });
      onInvited(result.status === 'assigned'
        ? t('team.assignedNotice', { email: result.email, count: targetIds.length })
        : t('team.invitedNotice', { email: result.email }), role);
    } catch (err) { setError(extractApiError(err).message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth={mode === 'bulk' ? 'md' : 'sm'} fullWidth>
      <DialogTitle>{t('team.assignTitle')}</DialogTitle>
      <Tabs value={mode} onChange={(_, v: 'single' | 'bulk') => setMode(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="single" label={t('units.tabSingle')} />
        <Tab value="bulk" label={t('res.import.tab')} />
      </Tabs>
      {mode === 'bulk' ? (
        <ResidentImportPanel
          developerId={developerId}
          clusterId={assignmentIds[0] ?? null}
          clusterPicker={developers.length > 1 || developerClusters.length > 1 ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {developers.length > 1 ? (
                <TextField select fullWidth label={t('team.developer')} value={developerId} onChange={(event) => {
                  const id = event.target.value;
                  const first = scope.clusters.find((cluster) => cluster.developer_id === id);
                  setDeveloperId(id);
                  setAssignmentIds(first ? [first.id] : []);
                }}>
                  {developers.map((developer) => <MenuItem key={developer.id} value={developer.id}>{developer.name}</MenuItem>)}
                </TextField>
              ) : null}
              {developerClusters.length > 1 ? (
                <TextField select fullWidth label={t('team.assignCluster')} value={assignmentIds[0] ?? ''}
                  onChange={(event) => setAssignmentIds([event.target.value])}>
                  {developerClusters.map((cluster) => <MenuItem key={cluster.id} value={cluster.id}>{cluster.name}</MenuItem>)}
                </TextField>
              ) : null}
            </Stack>
          ) : undefined}
          existingEmails={existingEmails}
          onClose={onClose}
          onImported={onImported}
        />
      ) : (
      <>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label={t('team.fullName')} value={fullName} onChange={(event) => setFullName(event.target.value)} required autoFocus />
            <TextField label={t('team.email')} type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            <FormControl fullWidth><InputLabel>{t('team.role')}</InputLabel>
              <Select label={t('team.role')} value={role} onChange={(event) => {
                setRole(event.target.value as TeamRole);
                setAssignmentIds((ids) => ids.length ? [ids[0]!] : developerClusters[0] ? [developerClusters[0].id] : []);
              }}>
                <MenuItem value="resident">{t('team.resident')}</MenuItem>
                {canAssignAdmins ? <MenuItem value="admin">{t('team.clusterAdmin')}</MenuItem> : null}
                {grantable.length ? <MenuItem value="internal">{t('team.staff')}</MenuItem> : null}
                <MenuItem value="security">{t('team.security')}</MenuItem>
                {identity?.admin_scope === 'PLATFORM' ? <MenuItem value="company_admin">{t('team.companyAdmin')}</MenuItem> : null}
              </Select>
            </FormControl>
            {developers.length > 1 ? (
              <FormControl fullWidth><InputLabel>{t('team.developer')}</InputLabel>
                <Select label={t('team.developer')} value={developerId} onChange={(event) => {
                  const id = event.target.value;
                  const first = scope.clusters.find((cluster) => cluster.developer_id === id);
                  setDeveloperId(id);
                  setAssignmentIds(first ? [first.id] : []);
                }}>
                  {developers.map((developer) => <MenuItem key={developer.id} value={developer.id}>{developer.name}</MenuItem>)}
                </Select>
              </FormControl>
            ) : null}
            {role === 'company_admin' ? <TextField
              label={t('team.assignClusters')} value={t('team.allClusters')} disabled
              helperText={t('team.companyAll', { count: developerClusters.length })}
            /> : role === 'admin' ? <FormControl fullWidth>
              <InputLabel>{t('team.assignClusters')}</InputLabel>
              <Select multiple label={t('team.assignClusters')} value={assignmentIds}
                renderValue={(ids) => ids.map((id) => developerClusters.find((cluster) => cluster.id === id)?.name ?? id).join(', ')}
                onChange={(event) => {
                  const value = event.target.value;
                  setAssignmentIds(typeof value === 'string' ? value.split(',') : value);
                }}>
                {developerClusters.map((cluster) => <MenuItem key={cluster.id} value={cluster.id}>
                  <Checkbox checked={assignmentIds.includes(cluster.id)} />{cluster.name}
                </MenuItem>)}
              </Select>
            </FormControl> : developerClusters.length > 1 ? <FormControl fullWidth>
              <InputLabel>{t('team.assignCluster')}</InputLabel>
              <Select label={t('team.assignCluster')} value={assignmentIds[0] ?? ''}
                onChange={(event) => setAssignmentIds([event.target.value])}>
                {developerClusters.map((cluster) => <MenuItem key={cluster.id} value={cluster.id}>{cluster.name}</MenuItem>)}
              </Select>
            </FormControl> : null}
            {role === 'internal' ? <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t('team.pageAccess')}</Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap>
                {grantable.map((item) => <FormControlLabel key={item.id}
                  control={<Checkbox size="small" checked={permissions.includes(item.id)} onChange={(event) => setPermissions((current) => event.target.checked ? [...current, item.id] : current.filter((value) => value !== item.id))} />}
                  label={t(item.labelKey)} />)}
              </Stack>
            </Box> : null}
            {/* Pick the block first, then the house number inside it. */}
            {role === 'resident' ? <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth><InputLabel>{t('team.block')}</InputLabel>
                <Select label={t('team.block')} value={block} onChange={(event) => { setBlock(event.target.value); setUnitId(''); }}>
                  {blocks.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={!block}><InputLabel>{t('team.unitNumber')}</InputLabel>
                <Select label={t('team.unitNumber')} value={unitId} onChange={(event) => setUnitId(event.target.value)}>
                  {unitsInBlock.map((unit) => <MenuItem key={unit.id} value={unit.id}>{unit.number}{!labelRepeatsAddress(unit.label, unit.block, unit.number) ? ` · ${unit.label}` : ''}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack> : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={() => void submit()} disabled={busy || !canSubmit}>
            {busy ? t('team.sending') : t('team.send')}
          </Button>
        </DialogActions>
      </>
      )}
    </Dialog>
  );
}
